import bootbox from "bootbox";
import { Form } from 'formsy-react-components';
import PropTypes from 'prop-types';
import React from 'react';
import { Button, ButtonGroup } from 'react-bootstrap';
import { defineMessages, FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { getUISettings } from '../app/UISettings.react.js';
import ChannelIcon from '../channel-defs/ChannelIcon.react';
import { compoundApplicationName } from '../fw/GridList.react';
import ScenarioChannelConnect from './ScenarioChannelConnect.react';
import { ChannelDef } from '../../common/types/types';

const messages = defineMessages({
    submit: {id: 'organisationCompanySearch.submit', 'defaultMessage': 'Save'},
    cancel: {id: 'cancel', 'defaultMessage': 'Cancel'},
    confirmDisableConfidential: {id: 'confirmDisableConfidential', 'defaultMessage': 'Disable data confidentiality mode?'},
    channelLockedAddingApi: {id: 'channelLockedAddingApi', 'defaultMessage': 'This channel can only be added through add-ons API'}
});

interface ChannelChooserProps {
 channelGroup?: {
  channels: object[];
  channelDef: ChannelDef;
    groupKey?: string;
 };
 connectCallback?: (...args: unknown[]) => unknown;
 forceConfigure?: boolean;
 intl: IntlShape;
 locked?: boolean;
 nextStep: (...args: unknown[]) => unknown;
 onChannelSelect: (...args: unknown[]) => unknown;
 preselectedChannelId?: string;
 scenario?: object;
}

class ChannelChooser extends React.Component<ChannelChooserProps> {
    static propTypes = {
        channelGroup: PropTypes.exact({
            channels: PropTypes.arrayOf(PropTypes.object).isRequired,
            channelDef: PropTypes.object.isRequired,
            groupKey: PropTypes.string,
        }),
        connectCallback: PropTypes.func,
        forceConfigure: PropTypes.bool,
        intl: PropTypes.object.isRequired,
        locked: PropTypes.bool,
        nextStep: PropTypes.func.isRequired,
        onChannelSelect: PropTypes.func.isRequired,
        preselectedChannelId: PropTypes.string,
        scenario: PropTypes.object,
    };

    static sortByName(channelA, channelB) {
        let aName = channelA.name ? channelA.name : channelA.channelDef.name;
        let bName = channelB.name ? channelB.name : channelB.channelDef.name;

        return aName.localeCompare(bName);
    }

    constructor(props) {
        super(props);

        const preselectedChannels = (props.locked && props.preselectedChannelId)
            ? props.channelGroup.channels.filter((item) => item.realId === props.preselectedChannelId)
            : props.channelGroup.channels;

        const channelsToRender = preselectedChannels.length > 0 ? preselectedChannels : props.channelGroup.channels;
        const isSinglePreselected = Boolean(props.locked && props.preselectedChannelId && channelsToRender.length === 1);

        this.state = {
            chooser: !(props.forceConfigure && isSinglePreselected),
            configureExistingChannel: Boolean(props.forceConfigure && isSinglePreselected),
        };
    }

    toggleChooser(showChooser: boolean = false, configureExistingChannel: boolean = false) {
        this.setState({
            chooser: showChooser,
            configureExistingChannel
        });
    }

    showConfirmDialog(channel) {
        return new Promise(resolve => {
            bootbox.confirm({
                message: this.props.intl.formatMessage(messages.confirmDisableConfidential),
                size: 'small',
                buttons: {
                    cancel: {
                        label: this.props.intl.formatMessage(messages.cancel),
                        className: "btn-default",
                    },
                    confirm: {
                        label: this.props.intl.formatMessage(messages.submit),
                        className: "btn-danger",
                    },
                },
                callback: (result) => {
                    if (result) {
                        this.props.onChannelSelect(this.props.channelGroup.channelDef, channel);
                        this.props.nextStep();

                        return resolve();
                    }

                    resolve();
                }
            });
        });
    }

    async channelChooseClick(channel) {
        if (this.props.locked && this.props.preselectedChannelId && channel.realId !== this.props.preselectedChannelId) {
            return Promise.resolve();
        }

        if (channel.hasError === true) {
            return this.showConfirmDialog(channel);
        }

        this.props.onChannelSelect(this.props.channelGroup.channelDef, channel);
        this.props.nextStep();
        return Promise.resolve();
    }

    getButtons() {
        let {channelGroup} = this.props;

        return this.props.channelGroup.channels
            .sort(ChannelChooser.sortByName)
            .map((channel, idx) => {
                const isLockedOut = Boolean(this.props.locked && this.props.preselectedChannelId && channel.realId !== this.props.preselectedChannelId);
                return (
                    <Button className={channel.isItemActive ? 'active' : ''} disabled={isLockedOut} key={idx} onClick={() => {
                        return this.channelChooseClick(channel);
                    }}>
                        <ChannelIcon channelDef={channel.channelDef || channelGroup.channelDef} size={32}/>&nbsp;
                        {compoundApplicationName(channelGroup.channelDef.name, channel.name)}
                    </Button>
                );
            }
        );
    }

    render() {
        let {channelGroup} = this.props;
        const preselectedChannels = (this.props.locked && this.props.preselectedChannelId)
            ? this.props.channelGroup.channels.filter((item) => item.realId === this.props.preselectedChannelId)
            : this.props.channelGroup.channels;

        const channelsToRender = preselectedChannels.length > 0 ? preselectedChannels : this.props.channelGroup.channels;
        let isOneItem = channelsToRender.length === 1;
        let channel;
        if (isOneItem) {
            channel = channelsToRender[0];
        }
        if (this.state.chooser) {
            return (
                <Form onSubmit={() => {this.props.nextStep();}}>
                    <div key="channelsToSelect">
                        <div>
                            {isOneItem
                                ? (
                                    <div>
                                        <FormattedMessage defaultMessage="Use already connected application:" id="scenarioApply.alreadyConnectedChannel"/>

                                        <div className="singleChannelName">
                                            <ChannelIcon channelDef={channel.channelDef || channelGroup.channelDef} size={32}/>&nbsp;
                                            {compoundApplicationName(channelGroup.channelDef.name, channel.name)}
                                        </div>

                                        {channel.lockedAdding && (!getUISettings().isUpgatesAddon && !getUISettings().isShoptetAddon) && (
                                            <div>
                                                <div className="text-warning">
                                                    <i className="fa fa-lock" />{" "}
                                                    {this.props.intl.formatMessage(messages.channelLockedAddingApi)}
                                                </div>
                                                <br />
                                            </div>
                                        )}

                                        <Button bsStyle="primary" onClick={() => {
                                            return this.channelChooseClick(channel);
                                        }}>
                                            <FormattedMessage defaultMessage="Next >" id="common.next" />
                                        </Button>
                                    </div>
                                )
                                : (<div>
                                    <FormattedMessage defaultMessage="Choose for which {name} should the scenario apply:" id="scenarioApply.channelsToSelect" values={{name: channelGroup.channelDef.name}}/><br />

                                    <ButtonGroup className="channelsToSelectButtonGroup" vertical>
                                        {this.getButtons()}
                                    </ButtonGroup>
                                </div>)
                            }
                            <br />
                            {(!this.props.locked && (!isOneItem || (channel && !channel.lockedAdding))) && (
                                <FormattedMessage defaultMessage="Or {link}" id="scenarioApply.or" values={{link: (
                                    <a href="#" onClick={(event) => {event.preventDefault(); this.toggleChooser(false, false);}}>
                                        <FormattedMessage defaultMessage="connect another {appName}" id="scenarioApply.connectAnother" values={{appName: channelGroup.channelDef.name}}/><br />
                                    </a>
                                )}}/>
                            )}
                        </div>
                    </div>
                </Form>
            );
        }

        const selectedChannel = this.state.configureExistingChannel && channelsToRender.length === 1 ? channelsToRender[0] : null;

        return (<ScenarioChannelConnect
            cancel={() => {this.toggleChooser(true, false);}}
            channelDef={channelGroup.channelDef}
            connectCallback={this.props.connectCallback}
            existingChannel={selectedChannel}
            scenario={this.props.scenario}
        />);
    }
}

export default injectIntl(ChannelChooser);
